import React, { useMemo } from 'react';
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, AlertCircle, Lightbulb, Target, Zap, Clock, Send, CheckCircle } from 'lucide-react';

const CampaignAnalytics = ({ campaigns, darkMode }) => {
  // Calcular métricas
  const metrics = useMemo(() => {
    const total = campaigns.length;
    const ativas = campaigns.filter(c => c.status_execucao === 'executando').length;
    const agendadas = campaigns.filter(c => c.scheduled_date).length;
    const comFluxo = campaigns.filter(c => c.fluxo_id).length;
    
    // Campanhas por mês (últimos 6 meses)
    const now = new Date();
    const monthlyData = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const count = campaigns.filter(c => {
        if (!c.create_at) return false;
        const created = new Date(c.create_at);
        return created.getMonth() === date.getMonth() && 
               created.getFullYear() === date.getFullYear();
      }).length;
      
      monthlyData.push({
        mes: date.toLocaleDateString('pt-BR', { month: 'short' }),
        campanhas: count
      });
    }
    
    // Distribuição por tipo
    const tipoData = [
      { name: 'Manual', value: campaigns.filter(c => c.tipo === 'manual').length },
      { name: 'Agendada', value: campaigns.filter(c => c.tipo === 'agendada').length },
      { name: 'Por Ação', value: campaigns.filter(c => c.tipo === 'por_acao').length }
    ].filter(d => d.value > 0);
    
    // Taxa de uso de fluxos
    const fluxoRate = total > 0 ? ((comFluxo / total) * 100).toFixed(1) : 0;
    
    // Campanhas recentes (últimos 7 dias)
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const recentes = campaigns.filter(c => {
      if (!c.create_at) return false;
      return new Date(c.create_at) >= sevenDaysAgo;
    }).length;
    
    return {
      total,
      ativas,
      agendadas,
      comFluxo,
      fluxoRate,
      recentes,
      monthlyData,
      tipoData,
      crescimento: monthlyData.length >= 2 ? 
        ((monthlyData[5].campanhas - monthlyData[4].campanhas) / (monthlyData[4].campanhas || 1) * 100).toFixed(1) : 0
    };
  }, [campaigns]);
  
  // Gerar insights automáticos
  const insights = useMemo(() => {
    const tips = [];
    
    // Insight 1: Uso de fluxos
    if (metrics.total > 0) {
      if (metrics.fluxoRate < 30) {
        tips.push({
          type: 'warning',
          icon: AlertCircle,
          title: 'Automatize com fluxos',
          message: `Apenas ${metrics.fluxoRate}% das suas campanhas usam fluxos. Fluxos automatizam respostas e aumentam conversão.`,
          action: 'Criar fluxo'
        });
      } else if (metrics.fluxoRate > 70) {
        tips.push({
          type: 'success',
          icon: Target,
          title: 'Excelente automação!',
          message: `${metrics.fluxoRate}% das campanhas usam fluxos. Você está maximizando a eficiência!`,
          action: 'Ver fluxos'
        });
      }
    }
    
    // Insight 2: Agendamento
    if (metrics.total > 0 && metrics.agendadas === 0) {
      tips.push({
        type: 'tip',
        icon: Clock,
        title: 'Agende para melhor resultado',
        message: 'Envie campanhas entre 9h-11h ou 14h-16h para maior taxa de abertura.',
        action: 'Agendar campanha'
      });
    } else if (metrics.agendadas > 0) {
      tips.push({
        type: 'success',
        icon: Clock,
        title: 'Planejamento ativo',
        message: `${metrics.agendadas} campanhas agendadas. Planejamento é chave para o sucesso!`,
        action: 'Ver agendadas'
      });
    }
    
    // Insight 3: Crescimento
    if (metrics.total >= 2) {
      if (metrics.crescimento > 20) {
        tips.push({
          type: 'success',
          icon: TrendingUp,
          title: 'Crescimento acelerado',
          message: `+${metrics.crescimento}% de campanhas no último mês. Mantenha o ritmo!`,
          action: 'Ver evolução'
        });
      } else if (metrics.crescimento < -20) {
        tips.push({
          type: 'warning',
          icon: TrendingDown,
          title: 'Atividade em queda',
          message: `${metrics.crescimento}% menos campanhas. Que tal reativar sua estratégia?`,
          action: 'Criar campanha'
        });
      }
    }
    
    // Insight 4: Atividade recente
    if (metrics.total > 0) {
      if (metrics.recentes === 0) {
        tips.push({
          type: 'warning',
          icon: AlertCircle,
          title: 'Sem atividade recente',
          message: 'Nenhuma campanha nos últimos 7 dias. Mantenha contato regular com seus clientes!',
          action: 'Criar campanha'
        });
      } else if (metrics.recentes >= 3) {
        tips.push({
          type: 'success',
          icon: Zap,
          title: 'Alta atividade',
          message: `${metrics.recentes} campanhas nos últimos 7 dias. Excelente frequência de comunicação!`,
          action: 'Ver recentes'
        });
      }
    }
    
    // Insight 5: Primeira campanha
    if (metrics.total === 0) {
      tips.push({
        type: 'tip',
        icon: Send,
        title: 'Comece agora',
        message: 'Crie sua primeira campanha e comece a engajar seus contatos!',
        action: 'Criar primeira campanha'
      });
    }
    
    // Insight 6: Diversificação
    if (metrics.total > 5 && metrics.tipoData.length === 1) {
      tips.push({
        type: 'tip',
        icon: Target,
        title: 'Diversifique suas campanhas',
        message: 'Teste diferentes tipos de campanhas (manual, agendada) para descobrir o que funciona melhor.',
        action: 'Explorar tipos'
      });
    }
    
    return tips.slice(0, 4); // Máximo 4 insights
  }, [metrics]);
  
  const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444'];
  
  return (
    <div className="space-y-6">
      {/* Métricas Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total de Campanhas"
          value={metrics.total}
          icon={Send}
          color="blue"
          darkMode={darkMode}
        />
        <MetricCard
          title="Em Execução"
          value={metrics.ativas}
          subtitle={`${metrics.total > 0 ? ((metrics.ativas / metrics.total) * 100).toFixed(0) : 0}% do total`}
          icon={CheckCircle}
          color="green"
          darkMode={darkMode}
        />
        <MetricCard
          title="Com Fluxos"
          value={metrics.comFluxo}
          subtitle={`${metrics.fluxoRate}% das campanhas`}
          icon={Target}
          color="purple"
          darkMode={darkMode}
        />
        <MetricCard
          title="Últimos 7 dias"
          value={metrics.recentes}
          subtitle={metrics.crescimento > 0 ? `+${metrics.crescimento}%` : `${metrics.crescimento}%`}
          icon={TrendingUp}
          color={metrics.crescimento >= 0 ? "green" : "red"}
          darkMode={darkMode}
        />
      </div>
      
      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Evolução Mensal */}
        <div className={`rounded-lg shadow p-6 ${
          darkMode ? "bg-zinc-800 border border-gray-700" : "bg-white border border-gray-200"
        }`}>
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? "text-white" : "text-gray-900"}`}>
            Evolução Mensal
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={metrics.monthlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#374151" : "#e5e7eb"} />
              <XAxis 
                dataKey="mes" 
                stroke={darkMode ? "#9ca3af" : "#6b7280"}
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke={darkMode ? "#9ca3af" : "#6b7280"}
                style={{ fontSize: '12px' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: darkMode ? "#1f2937" : "#ffffff",
                  border: `1px solid ${darkMode ? "#374151" : "#e5e7eb"}`,
                  borderRadius: "8px",
                  color: darkMode ? "#ffffff" : "#000000"
                }}
              />
              <Line 
                type="monotone" 
                dataKey="campanhas" 
                stroke="#10b981" 
                strokeWidth={2}
                dot={{ fill: "#10b981", r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        
        {/* Distribuição por Tipo */}
        <div className={`rounded-lg shadow p-6 ${
          darkMode ? "bg-zinc-800 border border-gray-700" : "bg-white border border-gray-200"
        }`}>
          <h3 className={`text-lg font-semibold mb-4 ${darkMode ? "text-white" : "text-gray-900"}`}>
            Distribuição por Tipo
          </h3>
          {metrics.tipoData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={metrics.tipoData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {metrics.tipoData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    backgroundColor: darkMode ? "#1f2937" : "#ffffff",
                    border: `1px solid ${darkMode ? "#374151" : "#e5e7eb"}`,
                    borderRadius: "8px"
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[250px]">
              <p className={darkMode ? "text-gray-400" : "text-gray-500"}>
                Nenhuma campanha criada ainda
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Insights e Sugestões */}
      {insights.length > 0 && (
        <div className={`rounded-lg shadow p-6 ${
          darkMode ? "bg-zinc-800 border border-gray-700" : "bg-white border border-gray-200"
        }`}>
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="h-5 w-5 text-yellow-500" />
            <h3 className={`text-lg font-semibold ${darkMode ? "text-white" : "text-gray-900"}`}>
              Insights e Sugestões
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {insights.map((insight, index) => (
              <InsightCard key={index} insight={insight} darkMode={darkMode} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ title, value, subtitle, icon: Icon, color, darkMode }) => {
  const colorClasses = {
    blue: darkMode ? "bg-blue-900/20 text-blue-400" : "bg-blue-100 text-blue-600",
    green: darkMode ? "bg-green-900/20 text-green-400" : "bg-green-100 text-green-600",
    purple: darkMode ? "bg-purple-900/20 text-purple-400" : "bg-purple-100 text-purple-600",
    red: darkMode ? "bg-red-900/20 text-red-400" : "bg-red-100 text-red-600",
  };
  
  return (
    <div className={`rounded-lg shadow p-6 ${
      darkMode ? "bg-zinc-800 border border-gray-700" : "bg-white border border-gray-200"
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className={`text-sm font-medium ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
            {title}
          </p>
          <p className={`text-3xl font-bold mt-2 ${darkMode ? "text-white" : "text-gray-900"}`}>
            {value}
          </p>
          {subtitle && (
            <p className={`text-xs mt-1 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              {subtitle}
            </p>
          )}
        </div>
        <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </div>
  );
};

const InsightCard = ({ insight, darkMode }) => {
  const Icon = insight.icon;
  const typeColors = {
    success: darkMode ? "border-green-700 bg-green-900/10" : "border-green-200 bg-green-50",
    warning: darkMode ? "border-yellow-700 bg-yellow-900/10" : "border-yellow-200 bg-yellow-50",
    tip: darkMode ? "border-blue-700 bg-blue-900/10" : "border-blue-200 bg-blue-50",
  };
  
  const iconColors = {
    success: "text-green-500",
    warning: "text-yellow-500",
    tip: "text-blue-500",
  };
  
  return (
    <div className={`rounded-lg border p-4 ${typeColors[insight.type]}`}>
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${iconColors[insight.type]}`} />
        <div className="flex-1">
          <h4 className={`font-semibold text-sm mb-1 ${darkMode ? "text-white" : "text-gray-900"}`}>
            {insight.title}
          </h4>
          <p className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
            {insight.message}
          </p>
        </div>
      </div>
    </div>
  );
};

export default CampaignAnalytics;
